import { MarkdownService as NgxMarkdownService } from 'ngx-markdown';

import html2canvas from 'html2canvas';

declare const mermaid: any;

import {
  IFileExportDriver,
  TExportOptions,
} from '@shared/services/file-export/file-export-interface';
import { FileImage } from '@shared/services/file-export/tools/file-image';

import { MermaidConverterService } from '@shared/services/mermaid-converter.service';

export class ImageToPdfDriver implements IFileExportDriver {
  private pages: string[] = [];
  private options: TExportOptions | undefined;
  private messages: any[] = [];
  private logoFile: FileImage | undefined;
  private markdownService: NgxMarkdownService | undefined;
  private mermaidConverter: MermaidConverterService | undefined;

  setPages(pages: string[]): this {
    this.pages = pages;
    return this;
  }

  setMessages(messages: any[]): this {
    this.messages = messages;
    return this;
  }

  // Method to inject markdown service
  setMarkdownService(markdownService: NgxMarkdownService): this {
    this.markdownService = markdownService;
    return this;
  }

  // Method to inject Mermaid converter service
  setMermaidConverter(mermaidConverter: MermaidConverterService): this {
    this.mermaidConverter = mermaidConverter;
    return this;
  }

  setOptions(options: TExportOptions): IFileExportDriver {
    this.options = options;
    return this;
  }

  async setLogo(filePath: string): Promise<any> {
    this.logoFile = new FileImage();
    if ((await this.logoFile.setLogo(filePath)) === false) {
      this.logoFile = undefined;
    }
    return Promise.resolve();
  }

  private async initializePdfMake(): Promise<any> {
    // Dynamic import to avoid initialization issues
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');

    // Get the actual pdfMake object (could be default export or direct export)
    const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;

    // Get the vfs fonts
    const vfs = (pdfFontsModule as any).pdfMake?.vfs ||
                (pdfFontsModule as any).default?.pdfMake?.vfs ||
                pdfFontsModule;

    // Initialize fonts - assign to vfs property of pdfMake
    if (pdfMake && typeof pdfMake === 'object') {
      pdfMake.vfs = vfs;
    }

    return pdfMake;
  }

  async generate(filename: string): Promise<void> {
    try {
      // Show loading overlay
      this.showLoadingOverlay();

      // Initialize pdfMake dynamically
      const pdfMake = await this.initializePdfMake();

      // Process content: either messages or pages
      let processedContent = '';
      if (this.messages.length > 0) {
        // For messages, we first need to convert them to HTML using the markdown service
        // to get the rendered Mermaid diagrams, then process those
        processedContent = await this.processMessagesWithRendering();
      } else if (this.pages.length > 0) {
        processedContent = await this.processPages();
      }

      if (!processedContent.trim()) {
        return;
      }

      // Convert processed markdown to HTML
      const htmlContent = await this.convertMarkdownToHtml(processedContent);

      // Convert HTML to PDFmake format
      const pdfMakeContent = await this.convertHtmlToPdfmake(htmlContent);

      // Create PDF document definition
      const docDefinition = this.createDocumentDefinition(pdfMakeContent);

      // Generate and download PDF
      pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);

      // Hide loading overlay after a short delay to allow download to start
      setTimeout(() => {
        this.hideLoadingOverlay();
      }, 1000);
    } catch (error) {
      this.hideLoadingOverlay();
      throw error;
    }
  }

  private async processMessagesWithRendering(): Promise<string> {
    if (!this.markdownService) {
      return this.processMessages();
    }

    const processedMessages: string[] = [];

    for (const message of this.messages) {
      // Add message header for better organization
      const authorName = message.author === 'user' ? 'You' : 'Assistant';
      const header = `## ${authorName}\n\n`;

      // Extract raw markdown content from message paragraphs
      let messageContent = '';
      if (message.paragraphs) {
        messageContent = message.paragraphs
          .map((paragraph: any) => paragraph.text || '')
          .filter((text: string) => text.trim())
          .join('\n\n');
      } else if (message.text) {
        messageContent = message.text;
      }

      if (messageContent.trim()) {
        try {
          // First, render the markdown to HTML to get rendered Mermaid diagrams
          const htmlContent = this.markdownService.parse(messageContent, {
            mermaid: true,
            disableSanitizer: true,
          });

          // Then convert the rendered SVGs to PNGs
          const processedHtml = await this.processMermaidDiagrams(htmlContent as string); // TODO check if it works

          // For final output, we'll work with the processed HTML and let html-to-pdfmake handle conversion
          processedMessages.push(header + processedHtml);
        } catch (error) {
          // Fallback to processing raw markdown
          const processedMarkdown = await this.processMermaidDiagrams(messageContent);
          processedMessages.push(header + processedMarkdown);
        }
      }
    }

    return processedMessages.join('\n\n---\n\n');
  }

  private async processMessages(): Promise<string> {
    const processedMessages: string[] = [];

    for (const message of this.messages) {
      let messageContent = '';

      // Add message header for better organization
      const authorName = message.author === 'user' ? 'You' : 'Assistant';
      const header = `## ${authorName}\n\n`;

      // Extract text from message paragraphs
      if (message.paragraphs) {
        messageContent = message.paragraphs
          .map((paragraph: any) => paragraph.text || '')
          .filter((text: string) => text.trim())
          .join('\n\n');
      } else if (message.text) {
        messageContent = message.text;
      }

      if (messageContent.trim()) {
        // Convert Mermaid diagrams to images in this message content
        const processedMessageContent = await this.processMermaidDiagrams(messageContent);
        processedMessages.push(header + processedMessageContent);
      }
    }

    return processedMessages.join('\n\n---\n\n');
  }

  private async processPages(): Promise<string> {
    const processedPages: string[] = [];

    for (const page of this.pages) {
      if (page && page.trim()) {
        // Check if this page contains HTML content (from export modal)
        const isHtmlContent = page.includes('<') && page.includes('>');

        if (isHtmlContent) {
          // For HTML content, we need to wait for Mermaid rendering and then process
          const processedPage = await this.processHtmlPageContent(page);
          processedPages.push(processedPage);
        } else {
          // Convert Mermaid diagrams to images in this page
          const processedPage = await this.processMermaidDiagrams(page);
          processedPages.push(processedPage);
        }
      }
    }

    return processedPages.join('\n\n---\n\n');
  }

  private async processHtmlPageContent(htmlContent: string): Promise<string> {
    // The HTML content from the export modal contains markdown that needs to be rendered
    // and then we need to wait for Mermaid diagrams to be rendered before processing
    return new Promise(resolve => {
      // Create a temporary container to render the HTML
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '0';
      tempContainer.style.left = '0';
      tempContainer.style.visibility = 'visible'; // Make visible for Mermaid rendering
      tempContainer.style.width = '800px';
      tempContainer.style.padding = '20px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.innerHTML = htmlContent;

      // Add to DOM so Mermaid can render
      document.body.appendChild(tempContainer);

      // Check for Mermaid elements and initialize rendering
      const mermaidElements = tempContainer.querySelectorAll(
        '.mermaid, pre code.language-mermaid, code.language-mermaid'
      );

      if (mermaidElements.length === 0) {
        // No Mermaid diagrams, process as-is
        const result = tempContainer.innerHTML;
        document.body.removeChild(tempContainer);
        resolve(result);
      } else {
        // Wait for Mermaid to render, then process SVGs
        if (typeof mermaid !== 'undefined') {
          try {
            // Initialize Mermaid elements
            mermaid.init(undefined, mermaidElements);

            // Wait for rendering to complete
            setTimeout(async () => {
              try {
                const processedHtml = await this.convertRenderedSvgToPng(tempContainer.innerHTML);
                document.body.removeChild(tempContainer);
                resolve(processedHtml);
              } catch (error) {
                document.body.removeChild(tempContainer);
                resolve(htmlContent); // Fallback to original content
              }
            }, 2000); // Give more time for complex diagrams
          } catch (mermaidError) {
            document.body.removeChild(tempContainer);
            resolve(htmlContent); // Fallback to original content
          }
        } else {
          document.body.removeChild(tempContainer);
          resolve(htmlContent);
        }
      }
    });
  }

  private async processMermaidDiagrams(content: string): Promise<string> {
    // Check if content is HTML (contains rendered SVG) or raw markdown
    const isHtmlContent = content.includes('<div class="mermaid"') || content.includes('<svg');

    if (isHtmlContent) {
      try {
        const result = await this.convertRenderedSvgToPng(content);
        return result;
      } catch (error) {
        return this.cleanMermaidBlocks(content);
      }
    } else {
      // For raw markdown, process Mermaid code blocks
      try {
        const result = await this.convertMermaidCodeBlocksToImages(content);
        return result;
      } catch (error) {
        return this.cleanMermaidBlocks(content);
      }
    }
  }

  private async convertMermaidCodeBlocksToImages(markdownContent: string): Promise<string> {
    // Find all Mermaid code blocks
    const mermaidRegex = /```mermaid\n([\s\S]*?)```/g;
    let result = markdownContent;
    const matches = Array.from(markdownContent.matchAll(mermaidRegex));

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const mermaidCode = match[1].trim();

      if (!mermaidCode) continue;

      try {
        const imageDataUrl = await this.renderSingleMermaidToPng(mermaidCode);

        if (imageDataUrl) {
          // Replace the code block with an image markdown
          const imageMarkdown = `![Mermaid Diagram](${imageDataUrl})`;
          result = result.replace(match[0], imageMarkdown);
        } else {
          // Keep the original block but mark it as failed
          result = result.replace(match[0], `**[Mermaid Diagram - Could not render]**`);
        }
      } catch (error) {
        result = result.replace(match[0], `**[Mermaid Diagram - Render error]**`);
      }
    }

    return result;
  }

  private async convertRenderedSvgToPng(htmlContent: string): Promise<string> {
    // Create a temporary DOM to parse the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Find all rendered mermaid SVGs
    const mermaidContainers = tempDiv.querySelectorAll('div.mermaid[data-processed="true"]');

    if (mermaidContainers.length === 0) {
      return htmlContent;
    }

    // Convert each SVG to PNG sequentially to avoid ID conflicts
    const results: { index: number; pngDataUrl: string | null }[] = [];

    // Process one SVG at a time to prevent duplicate IDs in DOM
    for (let containerIndex = 0; containerIndex < mermaidContainers.length; containerIndex++) {
      const container = mermaidContainers[containerIndex];
      const svgElement = container.querySelector('svg');

      if (svgElement) {
        // Wait for each conversion to complete before starting the next one
        const pngDataUrl = await this.convertSingleSvgToPng(svgElement);
        results.push({
          index: containerIndex,
          pngDataUrl,
        });

        // Small delay to ensure DOM cleanup between conversions
        await new Promise(resolve => setTimeout(resolve, 50));
      } else {
        results.push({
          index: containerIndex,
          pngDataUrl: null,
        });
      }
    }

    // Replace SVG containers with PNG images
    results.forEach(({ index, pngDataUrl }) => {
      const container = mermaidContainers[index];
      if (pngDataUrl && container) {
        // Replace the entire mermaid container with an img tag
        const imgElement = document.createElement('img');
        imgElement.src = pngDataUrl;
        imgElement.alt = 'Mermaid Diagram';

        // Use simple, safe styling to avoid complex calculations that could create corrupted numbers
        imgElement.style.cssText = 'width: 450px; height: auto; display: block; margin: 15px 0;';

        // Add fixed dimensions to prevent coordinate issues in html-to-pdfmake
        imgElement.setAttribute('width', '450');
        imgElement.setAttribute('data-diagram-type', 'mermaid');

        container.parentNode?.replaceChild(imgElement, container);
      } else {
        // Failed to convert SVG, keeping original
      }
    });

    return tempDiv.innerHTML;
  }

  private async renderSingleMermaidToPng(mermaidCode: string): Promise<string | null> {
    return new Promise(resolve => {
      try {
        // Check if Mermaid is available
        if (typeof mermaid === 'undefined') {
          resolve(null);
          return;
        }

        // Validate Mermaid syntax
        try {
          const isValid = mermaid.parse(mermaidCode);
          if (!isValid) {
            resolve(null);
            return;
          }
        } catch (parseError) {
          resolve(null);
          return;
        }

        // Generate unique ID
        const elementId =
          'mermaid-diagram-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

        // Create temporary div element for rendering
        const tempDiv = document.createElement('div');
        tempDiv.id = elementId;
        tempDiv.style.visibility = 'visible';
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '0';
        tempDiv.style.left = '0';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '20px';
        tempDiv.style.border = '1px solid #ccc';
        document.body.appendChild(tempDiv);

        // Render the diagram using the same pattern as MermaidConverterService
        mermaid.render(
          elementId,
          mermaidCode,
          async (svgCode: string) => {
            try {
              // Insert SVG into temp div
              tempDiv.innerHTML = svgCode;

              // Wait for SVG to fully render
              await new Promise(resolve => setTimeout(resolve, 500));

              // Capture as image using imported html2canvas
              const canvas = await html2canvas(tempDiv);

              // Convert to base64 image
              const imageDataUrl = canvas.toDataURL('image/png');

              // Clean up the temporary element
              if (document.body.contains(tempDiv)) {
                document.body.removeChild(tempDiv);
              }

              resolve(imageDataUrl);
            } catch (error) {
              // Clean up on error
              if (document.body.contains(tempDiv)) {
                document.body.removeChild(tempDiv);
              }
              resolve(null);
            }
          },
          tempDiv
        );
      } catch (error) {
        resolve(null);
      }
    });
  }

  private async convertSingleSvgToPng(svgElement: SVGElement): Promise<string | null> {
    try {
      // Create a temporary container for the SVG
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '0';
      tempContainer.style.left = '0';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.border = '1px solid #ddd';
      tempContainer.style.visibility = 'visible';

      // Clone the SVG to avoid affecting the original
      const svgClone = svgElement.cloneNode(true) as SVGElement;
      tempContainer.appendChild(svgClone);

      // Add to DOM temporarily
      document.body.appendChild(tempContainer);

      // Wait a moment for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture with html2canvas
      const canvas = await html2canvas(tempContainer);

      // Convert to PNG data URL
      const pngDataUrl = canvas.toDataURL('image/png');

      // Clean up
      document.body.removeChild(tempContainer);

      return pngDataUrl;
    } catch (error) {
      return null;
    }
  }

  private cleanMermaidBlocks(content: string): string {
    // Remove Mermaid code blocks that weren't converted to images
    // This prevents them from appearing as raw text in the PDF
    const mermaidBlockRegex = /```mermaid\s*\n([\s\S]*?)\n```/g;
    const cleanedContent = content.replace(mermaidBlockRegex, () => {
      return `**[Mermaid Diagram]**\n\n_Note: Diagram could not be rendered in PDF format_`;
    });

    return cleanedContent;
  }

  private async convertMarkdownToHtml(content: string): Promise<string> {
    // Check if content is already HTML (contains HTML tags)
    const isAlreadyHtml = content.includes('<') && content.includes('>');

    if (isAlreadyHtml) {
      return content;
    }

    // Content should already have Mermaid diagrams processed
    if (!this.markdownService) {
      // Fallback: basic markdown to HTML conversion
      return this.basicMarkdownToHtml(content);
    }

    try {
      // Use ngx-markdown service to convert markdown to HTML
      // Disable Mermaid since we already processed diagrams
      return this.markdownService.parse(content, {
        mermaid: false,
        disableSanitizer: true,
      });
    } catch (error) {
      return this.basicMarkdownToHtml(content);
    }
  }

  private basicMarkdownToHtml(markdown: string): string {
    // Basic fallback conversion
    return markdown
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      .replace(/\n\n/gim, '</p><p>')
      .replace(/^(.*)$/gim, '<p>$1</p>')
      .replace(/<p><\/p>/gim, '');
  }

  private async convertHtmlToPdfmake(html: string): Promise<any> {
    try {
      // Pre-process HTML to handle images safely
      const safeHtml = this.preprocessHtmlForPdfmake(html);

      // Dynamically import html-to-pdfmake
      const htmlToPdfmakeModule = await import('html-to-pdfmake');
      const htmlToPdfmake = htmlToPdfmakeModule.default || htmlToPdfmakeModule;

      // Use html-to-pdfmake to convert HTML to PDFmake format
      const converted = htmlToPdfmake(safeHtml, {
        defaultStyles: this.getDefaultStyles(),
        tableAutoSize: true,
        removeExtraBlanks: true,
        window: window as any, // Provide window object for browser environment
        imagesByReference: false, // Embed images directly instead of by reference
        ignoreStyles: ['max-width', 'width', 'height'], // Ignore problematic styles
      });

      return Array.isArray(converted) ? converted : [converted];
    } catch (error) {
      // Fallback: return as simple text
      return [{ text: html.replace(/<[^>]*>/g, '') }];
    }
  }

  private preprocessHtmlForPdfmake(html: string): string {
    // Remove all IDs from HTML elements to prevent duplicate ID conflicts
    let processedHtml = this.removeAllIdsFromHtml(html);

    // Replace image data URLs with placeholders to prevent coordinate corruption
    const imgRegex = /<img[^>]+src="data:image\/[^"]*"[^>]*>/g;
    const imgMatches = processedHtml.match(imgRegex);

    if (imgMatches) {
      imgMatches.forEach(imgTag => {
        // Extract the data URL
        const srcMatch = imgTag.match(/src="(data:image\/[^"]*)"/);
        if (srcMatch && srcMatch[1]) {
          const dataUrl = srcMatch[1];

          // Create a simple, safe replacement that PDFmake can handle with proper margins
          // PDF pageMargins are [40, 80, 40, 60] so content width is ~515px (595-40-40)
          const safeImgTag = `<div style="text-align: center; margin: 15px 0; padding: 0;">
            <img src="${dataUrl}" width="450"/>
          </div>`;

          processedHtml = processedHtml.replace(imgTag, safeImgTag);
        }
      });
    }

    return processedHtml;
  }

  private removeAllIdsFromHtml(html: string): string {
    // Remove id attributes from all HTML elements using regex
    // This prevents duplicate ID conflicts when processing multiple chats
    return html.replace(/\s+id\s*=\s*["'][^"']*["']/gi, '');
  }

  private getDefaultStyles(): any {
    return {
      h1: {
        fontSize: 24,
        bold: true,
        margin: [0, 20, 0, 15],
        color: '#2c3e50',
      },
      h2: {
        fontSize: 20,
        bold: true,
        margin: [0, 15, 0, 12],
        color: '#34495e',
      },
      h3: {
        fontSize: 16,
        bold: true,
        margin: [0, 12, 0, 10],
        color: '#34495e',
      },
      h4: {
        fontSize: 14,
        bold: true,
        margin: [0, 10, 0, 8],
        color: '#34495e',
      },
      h5: {
        fontSize: 12,
        bold: true,
        margin: [0, 8, 0, 6],
        color: '#34495e',
      },
      h6: {
        fontSize: 11,
        bold: true,
        margin: [0, 6, 0, 5],
        color: '#34495e',
      },
      p: {
        fontSize: 11,
        lineHeight: 1.5,
        margin: [0, 0, 0, 8],
        alignment: 'justify',
      },
      strong: {
        bold: true,
      },
      em: {
        italics: true,
      },
      ul: {
        margin: [0, 5, 0, 8],
      },
      ol: {
        margin: [0, 5, 0, 8],
      },
      li: {
        margin: [0, 2, 0, 2],
      },
      table: {
        margin: [0, 10, 0, 10],
      },
      th: {
        bold: true,
        fillColor: '#f8f9fa',
        margin: [4, 4, 4, 4],
      },
      td: {
        margin: [4, 4, 4, 4],
      },
      a: {
        color: '#3498db',
        decoration: 'underline',
      },
      blockquote: {
        margin: [20, 10, 0, 10],
        italics: true,
        color: '#7f8c8d',
      },
    };
  }

  private createDocumentDefinition(content: any): any {
    const docDefinition: any = {
      content,
      styles: this.getDefaultStyles(),
      pageSize: 'A4',
      pageMargins: [40, 80, 40, 60],
      defaultStyle: {
        fontSize: 11,
        font: 'Roboto',
      },
    };

    // Add header with logo if provided
    if (this.options?.headerLogo && this.logoFile?.logoBase) {
      docDefinition.header = {
        image: this.logoFile.logoBase,
        width: 120,
        alignment: 'center',
        margin: [0, 20, 0, 20],
      };
    }

    // Add footer with page numbers and confidentiality clause
    docDefinition.footer = (currentPage: number, pageCount: number) => {
      const footerContent: any[] = [];

      // Add confidentiality clause on the left
      if (this.options?.confidentialityClause && this.options?.confidentialityClauseText) {
        footerContent.push({
          text: this.options.confidentialityClauseText,
          fontSize: 9,
          color: '#666666',
          alignment: 'left',
          margin: [40, 10, 0, 10],
        });
      }

      // Add page numbers on the right
      if (this.options?.pageNumber) {
        footerContent.push({
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 10,
          alignment: 'right',
          margin: [0, 10, 40, 10],
        });
      }

      return {
        columns: [footerContent[0] || '', footerContent[1] || ''],
      };
    };

    return docDefinition;
  }

  private showLoadingOverlay(): void {
    // Create loading overlay element
    const overlay = document.createElement('div');
    overlay.id = 'pdf-loading-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    `;

    // Create loading content
    const content = document.createElement('div');
    content.style.cssText = `
      text-align: center;
      max-width: 400px;
      padding: 40px;
    `;

    // Add spinner
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 50px;
      height: 50px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      border-top: 3px solid white;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px auto;
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    // Add message
    const message = document.createElement('h2');
    message.textContent = 'Your file is getting ready...';
    message.style.cssText = `
      font-size: 24px;
      font-weight: 300;
      margin: 0 0 10px 0;
      color: white;
    `;

    const subMessage = document.createElement('p');
    subMessage.textContent = 'Processing diagrams and generating PDF';
    subMessage.style.cssText = `
      font-size: 16px;
      margin: 0;
      opacity: 0.8;
      color: white;
    `;

    content.appendChild(spinner);
    content.appendChild(message);
    content.appendChild(subMessage);
    overlay.appendChild(content);

    document.body.appendChild(overlay);
  }

  private hideLoadingOverlay(): void {
    const overlay = document.getElementById('pdf-loading-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  cleanUp(): void {
    this.pages = [];
    this.messages = [];
    this.options = undefined;
    this.logoFile = undefined;
    this.markdownService = undefined;
    this.mermaidConverter = undefined;
    this.hideLoadingOverlay();
  }
}
